import React, { useState, useEffect } from 'react';
import { 
  Smartphone, Tablet, Monitor, Tv, Trash2, RefreshCw,
  Shield, AlertCircle, CheckCircle, Infinity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

// Get icon for platform
const getPlatformIcon = (platform) => {
  switch (platform?.toLowerCase()) {
    case 'ios':
      return <Smartphone className="h-4 w-4" />;
    case 'android':
      return <Smartphone className="h-4 w-4" />;
    case 'roku':
    case 'appletv':
    case 'firestick':
      return <Tv className="h-4 w-4" />;
    case 'web':
      return <Monitor className="h-4 w-4" />;
    default:
      return <Tablet className="h-4 w-4" />;
  }
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format relative time
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
  if (diffMins < 10080) return `${Math.floor(diffMins / 1440)}d ago`;
  return formatDate(dateStr);
};

// Device card component
const DeviceCard = ({ device, onRemove }) => {
  const isActive = device.is_active;
  
  return (
    <div className={`flex items-start gap-4 p-4 rounded-lg border ${
      isActive ? 'border-green-500/30 bg-green-500/5' : 'border-border bg-secondary/30'
    }`}>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
        isActive ? 'bg-green-500/20 text-green-500' : 'bg-secondary text-muted-foreground'
      }`}>
        {getPlatformIcon(device.platform)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium truncate">{device.device_name || device.device_model || 'Unknown Device'}</p>
          {isActive && (
            <Badge className="bg-green-500/20 text-green-400 text-xs">Active</Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          {device.device_model} • {device.platform?.toUpperCase()}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {device.os_version && `${device.os_version} • `}
          App {device.app_version || 'N/A'}
        </p>
        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
          <span>First seen: {formatDate(device.first_seen)}</span>
          <span>Last seen: {formatRelativeTime(device.last_seen)}</span>
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="text-destructive hover:bg-destructive/10 hover:text-destructive shrink-0"
        onClick={() => onRemove(device)}
        data-testid={`remove-device-${device.device_id}`}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};

export function UserDevices({ username }) {
  const [devices, setDevices] = useState([]);
  const [userSettings, setUserSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deviceToRemove, setDeviceToRemove] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeSessions, setActiveSessions] = useState([]);

  // Form state for user settings
  const [formData, setFormData] = useState({
    custom_device_limit: '',
    custom_concurrent_limit: '',
    bypass_device_limit: false,
    bypass_concurrent_limit: false,
    admin_notes: ''
  });

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [devicesData, userData, sessionsData] = await Promise.all([
        api.getUserDevices(username),
        api.getUser(username),
        api.getSessions({ active_only: true, user: username })
      ]);
      
      setDevices(devicesData.devices || devicesData || []);
      setUserSettings(userData);
      setActiveSessions(sessionsData.sessions || sessionsData || []);
      
      // Initialize form with user data
      setFormData({
        custom_device_limit: userData.custom_device_limit ?? '',
        custom_concurrent_limit: userData.custom_concurrent_limit ?? '',
        bypass_device_limit: userData.bypass_device_limit ?? false,
        bypass_concurrent_limit: userData.bypass_concurrent_limit ?? false,
        admin_notes: userData.admin_notes ?? ''
      });
      
      setHasChanges(false);
    } catch (err) {
      setError(err.message || 'Failed to load user data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (username) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await api.updateUser(username, {
        custom_device_limit: formData.custom_device_limit === '' ? null : parseInt(formData.custom_device_limit),
        custom_concurrent_limit: formData.custom_concurrent_limit === '' ? null : parseInt(formData.custom_concurrent_limit),
        bypass_device_limit: formData.bypass_device_limit,
        bypass_concurrent_limit: formData.bypass_concurrent_limit,
        admin_notes: formData.admin_notes || null
      });
      toast.success('User settings saved');
      setHasChanges(false);
      fetchData(); // Refresh to get effective limits
    } catch (err) {
      toast.error(err.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveDevice = async () => {
    if (!deviceToRemove) return;
    setIsRemoving(true);
    try {
      await api.removeDevice(deviceToRemove.device_id);
      toast.success('Device removed successfully');
      setDeviceToRemove(null);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to remove device');
    } finally {
      setIsRemoving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Card className="border-border bg-card">
          <CardContent className="p-6">
            <div className="h-32 animate-pulse rounded bg-muted" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive/50 bg-destructive/10">
        <CardContent className="flex items-center gap-4 p-6">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <div className="flex-1">
            <p className="font-medium">Failed to load user data</p>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const effectiveDeviceLimit = userSettings?.effective_device_limit ?? 4;
  const effectiveConcurrentLimit = userSettings?.effective_concurrent_limit ?? 4;
  const deviceCount = devices.length;
  const activeSessionCount = activeSessions.length;

  return (
    <div className="space-y-6">
      {/* Limits Overview */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Limits & Overrides
          </CardTitle>
          <CardDescription>
            Configure device and stream limits for {username}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-secondary/30">
              <p className="text-sm text-muted-foreground">Devices</p>
              <p className="text-2xl font-bold">
                {deviceCount} / {formData.bypass_device_limit ? (
                  <Infinity className="inline h-6 w-6" />
                ) : effectiveDeviceLimit}
              </p>
              {deviceCount >= effectiveDeviceLimit && !formData.bypass_device_limit && (
                <Badge className="mt-1 bg-yellow-500/20 text-yellow-400 text-xs">At Limit</Badge>
              )}
            </div>
            <div className="p-4 rounded-lg bg-secondary/30">
              <p className="text-sm text-muted-foreground">Concurrent Streams</p>
              <p className="text-2xl font-bold">
                {activeSessionCount} / {formData.bypass_concurrent_limit ? (
                  <Infinity className="inline h-6 w-6" />
                ) : effectiveConcurrentLimit}
              </p>
              {activeSessionCount > 0 && (
                <Badge className="mt-1 bg-green-500/20 text-green-400 text-xs">
                  {activeSessionCount} Active
                </Badge>
              )}
            </div>
          </div>

          {/* Override Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Bypass Device Limit</Label>
                  <p className="text-xs text-muted-foreground">Allow unlimited devices</p>
                </div>
                <Switch
                  checked={formData.bypass_device_limit}
                  onCheckedChange={(checked) => handleFormChange('bypass_device_limit', checked)}
                  data-testid="bypass-device-limit-switch"
                />
              </div>
              {!formData.bypass_device_limit && (
                <div className="space-y-2">
                  <Label htmlFor="custom_device_limit">Custom Device Limit</Label>
                  <Input
                    id="custom_device_limit"
                    type="number"
                    min="1"
                    max="20"
                    placeholder="Use default"
                    value={formData.custom_device_limit}
                    onChange={(e) => handleFormChange('custom_device_limit', e.target.value)}
                    data-testid="custom-device-limit-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave empty to use tier/global default ({effectiveDeviceLimit})
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Bypass Concurrent Limit</Label>
                  <p className="text-xs text-muted-foreground">Allow unlimited streams</p>
                </div>
                <Switch
                  checked={formData.bypass_concurrent_limit}
                  onCheckedChange={(checked) => handleFormChange('bypass_concurrent_limit', checked)}
                  data-testid="bypass-concurrent-limit-switch"
                />
              </div>
              {!formData.bypass_concurrent_limit && (
                <div className="space-y-2">
                  <Label htmlFor="custom_concurrent_limit">Custom Concurrent Limit</Label>
                  <Input
                    id="custom_concurrent_limit"
                    type="number"
                    min="1"
                    max="10"
                    placeholder="Use default"
                    value={formData.custom_concurrent_limit}
                    onChange={(e) => handleFormChange('custom_concurrent_limit', e.target.value)}
                    data-testid="custom-concurrent-limit-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave empty to use tier/global default ({effectiveConcurrentLimit})
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Admin Notes */}
          <div className="space-y-2">
            <Label htmlFor="admin_notes">Admin Notes</Label>
            <Textarea
              id="admin_notes"
              placeholder="Notes about this user (VIP, test account, etc.)"
              value={formData.admin_notes}
              onChange={(e) => handleFormChange('admin_notes', e.target.value)}
              className="h-20"
              data-testid="admin-notes-textarea"
            />
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSaveSettings}
              disabled={!hasChanges || isSaving}
              data-testid="save-user-settings-btn"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Registered Devices */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            Registered Devices
            <Badge variant="secondary" className="ml-2">{devices.length}</Badge>
          </CardTitle>
          <CardDescription>
            Devices registered to this account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {devices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Smartphone className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium">No devices registered</p>
              <p className="text-sm text-muted-foreground">
                Devices will appear here when the user logs in from an app
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {devices.map((device, i) => (
                <DeviceCard
                  key={device.device_id || i}
                  device={device}
                  onRemove={setDeviceToRemove}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Remove Device Dialog */}
      <Dialog open={!!deviceToRemove} onOpenChange={() => setDeviceToRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Device</DialogTitle>
            <DialogDescription>
              This will sign out the device and remove it from the user's registered devices. 
              They will need to log in again on this device.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {deviceToRemove && (
              <div className="p-4 rounded-lg bg-secondary/30">
                <p className="font-medium">{deviceToRemove.device_name || deviceToRemove.device_model}</p>
                <p className="text-sm text-muted-foreground">
                  {deviceToRemove.platform?.toUpperCase()} • {deviceToRemove.device_model}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Device ID: {deviceToRemove.device_id?.slice(0, 20)}...
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeviceToRemove(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRemoveDevice}
              disabled={isRemoving}
            >
              {isRemoving ? 'Removing...' : 'Remove Device'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UserDevices;
